<script setup lang="ts">
  import { matInfo } from '@quasar/extras/material-icons'
  import dayjs from 'dayjs'

  import { getCurrentSumOfUsableCoinsStatisticsCoinsUsableCurrentSumGet } from '@/shared/api/intranet'
  import { useFiltersByUrlParams } from '@/shared/lib/useFiltersByUrlParams'

  import type { MetricsType } from '@/entities/metrics'

  import { AllocatedActiveCoinsTable } from '@/views/metrics/allocatedActiveCoins'
  import { SpentCoinsPerMonthTable } from '@/views/metrics/spentCoinsPerMonth'
  import { TransferStatsPerMonthTable } from '@/views/metrics/transferStatsPerMonth'
  import { UsableAveragePerMonthTable } from '@/views/metrics/usableAvaragePerMonth'
  import { UsableCoinsPerUserTable } from '@/views/metrics/usableCoinsPerUser'
  import { UserEngagementPerMonthTable } from '@/views/metrics/userEngagementPerMonth'

  definePage({
    meta: {
      layout: 'baseNew',
      adminRoute: true,
    },
  })

  const CURRENT_YEAR = new Date().getFullYear()
  const YEAR_OPTIONS = Array.from({ length: 11 }, (_, i) => ({
    id: `${CURRENT_YEAR - i}-01-01`,
    name: CURRENT_YEAR - i,
  }))

  const { t } = useI18n()
  useHead({
    title: () => t('navigation_item_metrics'),
  })
  const { filters: filtersByUrlParams, updateFilters } = useFiltersByUrlParams()

  const { data: walletBalance, isFetching } = useQuery({
    queryKey: ['walletBalance'],
    queryFn: () => {
      return getCurrentSumOfUsableCoinsStatisticsCoinsUsableCurrentSumGet()
    },
    select: (response) => {
      if (!response.data) {
        return 0
      }
      return Intl.NumberFormat('ru-RU').format(response.data.count)
    },
  })

  const metrics = computed<
    {
      description: string
      id: MetricsType
      name: string
      table:
        | typeof AllocatedActiveCoinsTable
        | typeof UsableCoinsPerUserTable
        | typeof SpentCoinsPerMonthTable
        | typeof UsableAveragePerMonthTable
        | typeof UserEngagementPerMonthTable
        | typeof TransferStatsPerMonthTable
    }[]
  >(() => [
    {
      id: 'metrics_transfer_balance',
      name: t('metrics_transfer_balance'),
      description: t('metrics_transfer_balance_description'),
      table: AllocatedActiveCoinsTable,
    },
    {
      id: 'metrics_users_balance',
      name: t('metrics_users_balance'),
      description: t('metrics_users_balance_description'),
      table: UsableCoinsPerUserTable,
    },
    {
      id: 'metrics_spend_coins',
      name: t('metrics_spend_coins'),
      description: t('metrics_spend_coins_description'),
      table: SpentCoinsPerMonthTable,
    },
    {
      id: 'metrics_usable_avarage_per_month',
      name: t('metrics_usable_avarage_per_month'),
      description: t('metrics_usable_avarage_per_month_description'),
      table: UsableAveragePerMonthTable,
    },
    {
      id: 'metrics_user_engagement_per_month',
      name: t('metrics_user_engagement_per_month'),
      description: t('metrics_user_engagement_per_month_description'),
      table: UserEngagementPerMonthTable,
    },
    {
      id: 'metrics_transfer_stats_per_month',
      name: t('metrics_transfer_stats_per_month'),
      description: t('metrics_transfer_stats_per_month_description'),
      table: TransferStatsPerMonthTable,
    },
  ])

  if (!filtersByUrlParams.value.metric) {
    updateFilters({ metric: metrics.value[0]!.id, year: YEAR_OPTIONS[0]!.id })
  }

  const handleUpdateMetric = (newMetric: string) => {
    updateFilters({ ...filtersByUrlParams.value, metric: newMetric, year: YEAR_OPTIONS[0]!.id })
  }
  const handleUpdateYear = (newYear: { id: string; name: number }) => {
    updateFilters({ ...filtersByUrlParams.value, year: newYear.id })
  }
  const showYearFilter = computed(() => filtersByUrlParams.value.metric !== 'metrics_users_balance')

  const selectedYear = computed(() => YEAR_OPTIONS.find((year) => year.id === filtersByUrlParams.value.year))

  const selectedMetric = computed(() => metrics.value.find((metric) => metric.id === filtersByUrlParams.value.metric)!)

  const formattedDate = computed(() => {
    return {
      startDate: dayjs(filtersByUrlParams.value.year as string).toDate(),
      endDate: dayjs(filtersByUrlParams.value.year as string)
        .endOf('year')
        .toDate(),
    }
  })
</script>
<template>
  <div class="metrics-page max-w-250 p-4">
    <div class="metrics-page__wallet">
      <p class="heading-md text-heading!">{{ $t('metrics_wallet') }}:</p>
      &nbsp;
      <div
        v-if="isFetching"
        class="metrics-page__wallet-balance"
      >
        <QSpinner />
      </div>
      <div
        v-else
        class="heading-md text-heading!"
      >
        {{ walletBalance }}
      </div>
    </div>

    <div class="metrics-page__filters">
      <div class="metrics-page__filters-item">
        <QSelect
          :model-value="filtersByUrlParams.metric"
          outlined
          :options="metrics"
          option-value="id"
          option-label="name"
          emit-value
          map-options
          :popup-content-style="{
            borderRadius: '8px',
            boxShadow: '0 5 10px #000000',
            backgroundColor: 'var(--background-bg-secondary)',
            color: 'var(--text-primary)',
          }"
          popup-content-class="metrics-page__filters-item-popup"
          @update:model-value="handleUpdateMetric"
        >
          <template #selected-item="{ opt }">
            <div>
              {{ opt.name }}
              <QIcon
                :name="matInfo"
                size="16px"
                color="primary"
              >
                <QTooltip
                  :delay="500"
                  max-width="300px"
                >
                  <div class="metrics-page__metric-description">
                    {{ opt.description }}
                  </div>
                </QTooltip>
              </QIcon>
            </div>
          </template>
        </QSelect>
      </div>
      <div
        v-if="showYearFilter"
        class="metrics-page__filters-item"
        style="width: 184px"
      >
        <QSelect
          :model-value="selectedYear"
          :options="YEAR_OPTIONS"
          outlined
          option-value="id"
          option-label="name"
          :popup-content-style="{
            borderRadius: '8px',
            boxShadow: '0 5 10px #000000',
            backgroundColor: 'var(--background-bg-secondary)',
            color: 'var(--text-primary)',
          }"
          popup-content-class="metrics-page__filters-item-popup"
          @update:model-value="handleUpdateYear"
        />
      </div>
    </div>
    <div
      v-if="filtersByUrlParams.metric"
      class="metrics-page__table"
    >
      <component
        :is="selectedMetric.table"
        :start-date="formattedDate.startDate"
        :end-date="formattedDate.endDate"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .metrics-page {
    width: 100%;
    &__wallet {
      @include font-montserrat-600(20px, 36px, var(--text-primary));
      @include flex-container(row, flex-start, center);
      margin-bottom: 16px;
    }
    &__wallet-balance {
      flex-shrink: 0;
    }
    &__metric-description {
      @include font-montserrat-400(12px, 16px);
      color: var(--white);
    }

    &__filters {
      @include flex-container(row, space-between, stretch, 16px);
      margin-bottom: 24px;
    }
    &__filters-item {
      &:nth-child(1) {
        flex-grow: 1;
      }
      .q-select {
        :deep() {
          .q-field {
            @include font-montserrat-400(12px, 20px, var(--text-primary));

            &__control {
              background-color: var(--background-bg-surface);
              border-radius: 8px;
            }
            &__native,
            &__prefix,
            &__suffix,
            &__input,
            &__marginal {
              color: var(--text-primary);
            }
          }
        }
        &.q-field--outlined {
          &.q-field--highlighted {
            :deep() {
              .q-field__control:after {
                border-color: var(--text-primary);
              }
            }
          }
        }
      }
    }
    &__table {
      :deep() {
        .q-table {
          @include font-montserrat-400(14px, 24px, var(--text-content));
          max-height: 768px;
          min-height: 150px;
          background-color: transparent;
          th {
            font-weight: 600;
          }

          &__card {
            border-radius: 4px;
          }
          &__bottom {
            background-color: var(--background-bg-secondary);
          }
        }
        thead {
          background-color: var(--background-bg-secondary);
        }
        tbody {
          background-color: var(--background-bg-surface);
        }
      }
    }
  }
  @include mobile {
    .metrics-page__wallet-balance {
      @include font-montserrat-600(16px, 24px, var(--text-primary));
    }

    .metrics-page__wallet {
      margin-bottom: 12px;
    }
    .metrics-page__filters {
      margin-bottom: 16px;
    }
  }
</style>

<style lang="scss">
  .metrics-page__filters-item-popup {
    .q-item--active {
      color: var(--text-primary);
    }
  }
</style>
